<template>
    <div>

        <Head :title="__('Dashboard')" />

        <!-- Call Center Stats Section -->
        <div class="call-stats bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 class="text-xl font-bold mb-6 text-gray-800 flex items-center">
                <svg class="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z">
                    </path>
                </svg>
                {{ __('Inbound Queue Daily Stats') }}
            </h2>

            <div class="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                <!-- Waiting Calls -->
                <div class="stat-card text-center p-4 rounded-lg bg-blue-50 border border-blue-100">
                    <svg class="w-6 h-6 mx-auto mb-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd"
                            d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414z"
                            clip-rule="evenodd"></path>
                    </svg>
                    <div class="text-3xl font-bold text-blue-600">{{ callStats.waiting }}</div>
                    <div class="text-sm text-blue-800 font-medium">{{ __('WAITING') }}</div>
                </div>

                <!-- Talking Calls -->
                <div class="stat-card text-center p-4 rounded-lg bg-green-50 border border-green-100">
                    <svg class="w-6 h-6 mx-auto mb-2 text-green-600" fill="currentColor" viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                            clip-rule="evenodd"></path>
                    </svg>
                    <div class="text-3xl font-bold text-green-600">{{ callStats.talking }}</div>
                    <div class="text-sm text-green-800 font-medium">{{ __('TALKING') }}</div>
                </div>

                <!-- Answered Calls -->
                <div class="stat-card text-center p-4 rounded-lg bg-purple-50 border border-purple-100">
                    <svg class="w-6 h-6 mx-auto mb-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"></path>
                    </svg>
                    <div class="text-3xl font-bold text-purple-600">{{ callStats.answered }}</div>
                    <div class="text-sm text-purple-800 font-medium">{{ __('ANSWERED') }}</div>
                </div>

                <!-- Abandoned Calls -->
                <div class="stat-card text-center p-4 rounded-lg bg-red-50 border border-red-100">
                    <svg class="w-6 h-6 mx-auto mb-2 text-red-600" fill="currentColor" viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clip-rule="evenodd"></path>
                    </svg>
                    <div class="text-3xl font-bold text-red-600">{{ callStats.abandoned }}</div>
                    <div class="text-sm text-red-800 font-medium">{{ __('ABANDONED') }}</div>
                </div>

                <!-- Total Offered -->
                <div class="stat-card text-center p-4 rounded-lg bg-indigo-50 border border-indigo-100">
                    <svg class="w-6 h-6 mx-auto mb-2 text-indigo-600" fill="currentColor" viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z">
                        </path>
                    </svg>
                    <div class="text-3xl font-bold text-indigo-600">{{ callStats.totalOffered }}</div>
                    <div class="text-sm text-indigo-800 font-medium">{{ __('TOTAL OFFERED') }}</div>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="quick-stats p-5 rounded-lg bg-gray-50 border border-gray-200">
                    <h3 class="font-semibold mb-4 text-gray-700 flex items-center">
                        <svg class="w-5 h-5 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd"
                                d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zm3 14a1 1 0 100-2 1 1 0 000 2z"
                                clip-rule="evenodd"></path>
                        </svg>
                        {{ __('QUICK CALLS') }}
                    </h3>
                    <div class="grid grid-cols-2 gap-4">
                        <div class="bg-white p-3 rounded border border-gray-100 flex items-start">
                            <svg class="w-4 h-4 mt-1 mr-2 text-blue-400" fill="currentColor" viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                    clip-rule="evenodd"></path>
                            </svg>
                            <div>
                                <div class="text-xs text-gray-500 mb-1">{{ __('ACTIVE QUICK CALLS') }}</div>
                                <div class="font-bold text-lg text-gray-800">{{ callStats.activeQuickCalls }}</div>
                            </div>
                        </div>
                        <div class="bg-white p-3 rounded border border-gray-100 flex items-start">
                            <svg class="w-4 h-4 mt-1 mr-2 text-green-400" fill="currentColor" viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clip-rule="evenodd"></path>
                            </svg>
                            <div>
                                <div class="text-xs text-gray-500 mb-1">{{ __('ANSWERED QUICK CALLS') }}</div>
                                <div class="font-bold text-lg text-gray-800">{{ callStats.answeredQuickCalls }}</div>
                            </div>
                        </div>
                        <div class="bg-white p-3 rounded border border-gray-100 flex items-start">
                            <svg class="w-4 h-4 mt-1 mr-2 text-red-400" fill="currentColor" viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clip-rule="evenodd"></path>
                            </svg>
                            <div>
                                <div class="text-xs text-gray-500 mb-1">{{ __('ABANDONED QUICK CALLS') }}</div>
                                <div class="font-bold text-lg text-gray-800">{{ callStats.abandonedQuickCalls }}</div>
                            </div>
                        </div>
                        <div class="bg-white p-3 rounded border border-gray-100 flex items-start">
                            <svg class="w-4 h-4 mt-1 mr-2 text-indigo-400" fill="currentColor" viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z">
                                </path>
                            </svg>
                            <div>
                                <div class="text-xs text-gray-500 mb-1">{{ __('TOTAL QUICK CALLS') }}</div>
                                <div class="font-bold text-lg text-gray-800">{{ callStats.totalQuickCalls }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="performance-stats p-5 rounded-lg bg-gray-50 border border-gray-200">
                    <h3 class="font-semibold mb-4 text-gray-700 flex items-center">
                        <svg class="w-5 h-5 mr-2 text-purple-500" fill="currentColor" viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd"
                                d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z"
                                clip-rule="evenodd"></path>
                        </svg>
                        {{ __('PERFORMANCE METRICS') }}
                    </h3>
                    <div class="grid grid-cols-2 gap-4">
                        <div class="bg-white p-3 rounded border border-gray-100 flex items-start">
                            <svg class="w-4 h-4 mt-1 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                    clip-rule="evenodd"></path>
                            </svg>
                            <div>
                                <div class="text-xs text-gray-500 mb-1">{{ __('AVERAGE HOLD TIME') }}</div>
                                <div class="font-bold text-lg text-gray-800">{{ callStats.avgHoldTime }}</div>
                            </div>
                        </div>
                        <div class="bg-white p-3 rounded border border-gray-100 flex items-start">
                            <svg class="w-4 h-4 mt-1 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z">
                                </path>
                                <path
                                    d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z">
                                </path>
                            </svg>
                            <div>
                                <div class="text-xs text-gray-500 mb-1">{{ __('AVERAGE TALK TIME') }}</div>
                                <div class="font-bold text-lg text-gray-800">{{ callStats.avgTalkTime }}</div>
                            </div>
                        </div>
                        <div class="bg-white p-3 rounded border border-gray-100 flex items-start">
                            <svg class="w-4 h-4 mt-1 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd"
                                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                    clip-rule="evenodd"></path>
                            </svg>
                            <div>
                                <div class="text-xs text-gray-500 mb-1">{{ __('ANSWER RATE') }}</div>
                                <div class="font-bold text-lg text-gray-800">{{ callStats.answerRate }}</div>
                            </div>
                        </div>
                        <div class="bg-white p-3 rounded border border-gray-100 flex items-start">
                            <svg class="w-4 h-4 mt-1 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd"
                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                    clip-rule="evenodd"></path>
                            </svg>
                            <div>
                                <div class="text-xs text-gray-500 mb-1">{{ __('ABANDON RATE') }}</div>
                                <div class="font-bold text-lg text-gray-800">{{ callStats.abandonRate }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Ticket Stats Section -->
        <div class="badge__items flex flex-col lg:flex-row gap-5">
            <div class="badge__item h-32 w-full lg:w-1/4 cursor-pointer"
                @click="goToLink(this.route('tickets', { 'type': 'new' }))">
                <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                    <div class="badge__info">
                        <span class="title">{{ __('New Tickets') }}</span>
                        <span class="number">{{ new_tickets }}</span>
                    </div>
                    <div class="a__right">
                        <div class="round_circle pr-3 rtl:pl-3">
                            <div class="pie animate"
                                :style="{ '--pie_val': parseInt((new_tickets * 100) / total_tickets) }"> {{
                                    parseInt((new_tickets * 100)/total_tickets) || 0 }}%</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="badge__item h-32 w-full lg:w-1/4 cursor-pointer"
                @click="goToLink(this.route('tickets', { 'type': 'open' }))">
                <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                    <div class="badge__info">
                        <span class="title">{{ __('Open Tickets') }}</span>
                        <span class="number">{{ opened_tickets }}</span>
                    </div>
                    <div class="a__right">
                        <div class="round_circle pr-3 rtl:pl-3">
                            <div class="pie animate"
                                :style="{ '--pie_val': parseInt((opened_tickets * 100) / total_tickets) }"> {{
                                    parseInt((opened_tickets * 100)/total_tickets) || 0 }}%</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="badge__item h-32 w-full lg:w-1/4 cursor-pointer"
                @click="goToLink(this.route('tickets', { 'search': 'close' }))">
                <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                    <div class="badge__info">
                        <span class="title">{{ __('Closed Tickets') }}</span>
                        <span class="number">{{ closed_tickets }}</span>
                    </div>
                    <div class="a__right">
                        <div class="round_circle pr-3 rtl:pl-3">
                            <div class="pie animate"
                                :style="{ '--pie_val': parseInt((closed_tickets * 100) / total_tickets) }"> {{
                                    parseInt((closed_tickets * 100)/total_tickets) || 0 }}%</div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="auth.user.role.slug !== 'customer'" class="badge__item h-32 w-full lg:w-1/4 cursor-pointer"
                @click="goToLink(this.route('tickets', { 'type': 'un_assigned' }))">
                <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                    <div class="badge__info">
                        <span class="title">{{ __('Unassigned Tickets') }}</span>
                        <span class="number">{{ un_assigned_tickets }}</span>
                    </div>
                    <div class="a__right">
                        <div class="round_circle pr-3 rtl:pl-3">
                            <div class="pie animate"
                                :style="{ '--pie_val': parseInt((un_assigned_tickets * 100) / total_tickets) }"> {{
                                    parseInt((un_assigned_tickets * 100)/total_tickets) || 0 }}%</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="response__details mt-8 flex gap-5 flex-col lg:flex-row" v-if="auth.user.role.slug !== 'customer'">
            <div class="w-full">
                <div class="r__wrapper flex flex-col pl-5 pr-5 pb-5 bg-white items-center rounded-lg shadow-lg rd">
                    <h2 class="th__ttl font-bold text-lg pt-3">{{ __('Ticket by department') }}</h2>
                    <div class="th__info flex flex-col pt-3">
                        <vc-donut background="white" foreground="grey" :size="120" unit="px" :thickness="45" has-legend
                            legend-placement="right" :sections="top_departments" :total="100" :start-angle="0"
                            :auto-adjust-text-size="true">
                        </vc-donut>
                    </div>
                </div>
            </div>
            <div class="w-full">
                <div class="r__wrapper flex flex-col pl-5 pr-5 pb-5 bg-white items-center rounded-lg shadow-lg rd">
                    <h2 class="th__ttl font-bold text-lg pt-3">{{ __('Ticket by type') }}</h2>
                    <div class="th__info flex flex-col pt-3">
                        <vc-donut background="white" foreground="grey" :size="120" unit="px" :thickness="45" has-legend
                            legend-placement="right" :sections="top_types" :total="100" :start-angle="0"
                            :auto-adjust-text-size="true">
                        </vc-donut>
                    </div>
                </div>
            </div>
            <div class="w-full">
                <div class="r__wrapper flex flex-col pl-5 pl-5 pb-5 bg-white items-center rounded-lg shadow-lg rd">
                    <h2 class="th__ttl font-bold text-lg pt-3">{{ __('Top ticket creator') }}</h2>
                    <div class="th__info flex flex-col pt-3">
                        <vc-donut background="white" foreground="grey" :size="120" unit="px" :thickness="45" has-legend
                            legend-placement="right" :sections="top_creators" :total="100" :start-angle="0"
                            :auto-adjust-text-size="true">
                        </vc-donut>
                    </div>
                </div>
            </div>
        </div>

        <div class="response__details flex flex-col lg:flex-row mt-8 gap-5" v-if="auth.user.role.slug !== 'customer'">
            <div class="flex gap-5 flex-col lg:flex-row lg:w-10/12">
                <div class="w-full">
                    <div class="r__wrapper flex flex-col p-5 bg-white rounded-lg shadow-lg rd">
                        <h2 class="th__ttl font-bold text-lg pt-3">{{ __('Ticket history') }}</h2>
                        <div class="th__info flex">
                            <span class="text-2xl font-bold"> {{ chart_line.this_month }} </span>
                            <span class="pt-2 text-xs pl-1 pr-1">/ {{ __('this month') }}</span>
                            <span class="pt-2 text-xs font-bold pl-2 pr-2">{{ __('last month') }} {{
                                chart_line.last_month }}</span>
                        </div>
                        <div v-if="months.length" class="flex w-full justify-center c__wrapper ">
                            <div class="c__line flex flex-col w-full items-center gap-3" v-for="cl in months">
                                <span class="cl__b" :style="{ '--cl_value': cl.value }"></span>
                                <span class="cl__n text-xs">{{ cl.month }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class=" rd w-full lg:w-2/5">
                    <div class="r__wrapper flex flex-col pl-5 pr-5 pb-5 bg-white rounded-lg shadow-lg rd">
                        <div class="res__info">
                            <span class="title">{{ __('First Response Time') }}</span>
                            <span class="res_avg">{{ __('Average') }}</span>
                            <div class="flex justify-start">
                                <div v-if="firstResponse.length" v-for="(fr, fri) in firstResponse"
                                    class="res_time pr-5" :key="fri">
                                    <span class="num">{{ fr[0] }}</span>
                                    <span class="text">{{ __(fr[1]) }}</span>
                                </div>
                                <div v-else class="res_time pr-5">
                                    0
                                </div>
                            </div>
                        </div>
                        <div class="tc__info">
                            <span class="title">{{ __('Last Response Time') }}</span>
                            <span class="res_avg">{{ __('Average') }}</span>
                            <div class="flex justify-start">
                                <div v-if="lastResponse.length" v-for="(fr, fri) in lastResponse" class="res_time pr-5"
                                    :key="fri">
                                    <span class="num">{{ fr[0] }}</span>
                                    <span class="text">{{ __(fr[1]) }}</span>
                                </div>
                                <div v-else class="res_time pr-5">
                                    0
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex gap-5 flex-col lg:w-2/12">
                <div class="badge__item h-32 w-full cursor-pointer" @click="goToLink(this.route('users'))">
                    <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                        <div class="badge__info">
                            <span class="title">{{ __('Customers') }}</span>
                            <span class="number">{{ total_customer }}</span>
                        </div>
                        <div class="a__right">
                            <icon name="pending_users" class="h-5 fill-gray-400 mr-5 ml-5" />
                        </div>
                    </div>
                </div>
                <div class="badge__item h-32 w-full cursor-pointer" @click="goToLink(this.route('contacts'))">
                    <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                        <div class="badge__info">
                            <span class="title">{{ __('Contacts') }}</span>
                            <span class="number">{{ total_contacts }}</span>
                        </div>
                        <div class="a__right">
                            <icon name="contact" class="h-5 fill-gray-400 mr-5 ml-5" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading Process -->
        <div v-if="loading" class="processing-overlay">
            <div class="background"></div>
            <div class="loader">
                <svg width="200px" height="200px" xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid"
                    style="background: none;">
                    <circle cx="75" cy="50" fill="#ffffff" r="6.39718">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.875s"></animate>
                    </circle>
                    <circle cx="67.678" cy="67.678" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.75s"></animate>
                    </circle>
                    <circle cx="50" cy="75" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.625s"></animate>
                    </circle>
                    <circle cx="32.322" cy="67.678" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.5s"></animate>
                    </circle>
                    <circle cx="25" cy="50" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.375s"></animate>
                    </circle>
                    <circle cx="32.322" cy="32.322" fill="#ffffff" r="4.80282">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.25s"></animate>
                    </circle>
                    <circle cx="50" cy="25" fill="#ffffff" r="6.40282">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.125s"></animate>
                    </circle>
                    <circle cx="67.678" cy="32.322" fill="#ffffff" r="7.99718">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="0s"></animate>
                    </circle>
                </svg>
            </div>
        </div>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'

export default {
    metaInfo: { title: 'Dashboard' },
    components: {
        Head,
        Icon,
        Link,
    },
    layout: Layout,
    props: {
        auth: Object,
        entries: Array,
        chart_line: Object,
        api_key: String,
        new_tickets: Number,
        total_tickets: Number,
        un_assigned_tickets: Number,
        opened_tickets: Number,
        closed_tickets: Number,
        first_response: Array,
        top_creators: Array,
        last_response: Array,
        top_departments: Array,
        top_types: Array,
        total_customer: Number,
        total_contacts: Number,
    },
    data() {
        return {
            errors: [],
            loading: false,
            firstResponse: [],
            lastResponse: [],
            months: [],
            callStats: {
                waiting: Math.floor(Math.random() * 10),
                talking: Math.floor(Math.random() * 15) + 5,
                answered: Math.floor(Math.random() * 300) + 100,
                abandoned: Math.floor(Math.random() * 10) + 1,
                totalOffered: Math.floor(Math.random() * 200) + 200,
                activeQuickCalls: Math.floor(Math.random() * 5),
                answeredQuickCalls: Math.floor(Math.random() * 20) + 5,
                abandonedQuickCalls: Math.floor(Math.random() * 5),
                totalQuickCalls: Math.floor(Math.random() * 30) + 10,
                avgHoldTime: `00:00:${Math.floor(Math.random() * 20).toString().padStart(2, '0')}`,
                avgTalkTime: `00:00:${Math.floor(Math.random() * 10 + 5).toString().padStart(2, '0')}`,
                answerRate: `${Math.floor(Math.random() * 30) + 70}%`,
                abandonRate: `${Math.floor(Math.random() * 10)}%`,
                unmanaged: Math.floor(Math.random() * 5)
            }
        }
    },
    created() {
        for (let i = 0; i < this.first_response.length; i++) {
            if (i % 2 === 0) {
                this.firstResponse = [...this.firstResponse, [this.first_response[i], this.first_response[i + 1]]]
            }
        }
        for (let i = 0; i < this.last_response.length; i++) {
            if (i % 2 === 0) {
                this.lastResponse = [...this.lastResponse, [this.last_response[i], this.last_response[i + 1]]]
            }
        }

        this.months = this.chart_line.previousMonths.map(m => {
            return { 'month': m, 'value': this.chart_line.months[m] ? ((this.chart_line.months[m] * 100) / this.chart_line.total) + '%' : '0%' }
        })
    },
    methods: {
        goToLink(link) {
            window.location.href = link;
        },
    },
}
</script>

<style scoped>
.call-stats {
    border: 1px solid #e2e8f0;
}

.stat-card {
    transition: all 0.3s ease;
    border: 1px solid transparent;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.quick-stats,
.performance-stats {
    border: 1px solid #e2e8f0;
}

/* Existing styles from original template */
.badge__items {
    margin-bottom: 1.5rem;
}

.l__items {
    padding: 1.5rem;
    height: 100%;
}

.badge__info {
    display: flex;
    flex-direction: column;
}

.badge__info .title {
    font-size: 0.875rem;
    color: #6b7280;
    margin-bottom: 0.5rem;
}

.badge__info .number {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
}

.round_circle {
    display: flex;
    align-items: center;
}

.pie {
    --size: 3.5rem;
    --pie_val: 0;
    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    background: conic-gradient(#3b82f6 calc(var(--pie_val) * 1%), #e5e7eb 0);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    color: #111827;
}

/* Ensure icons are properly sized and aligned */
.stat-card svg,
.quick-stats svg,
.performance-stats svg {
    flex-shrink: 0;
    display: inline-block;
    vertical-align: middle;
}

/* Add some hover effects */
.stat-card:hover svg {
    transform: scale(1.1);
    transition: transform 0.2s ease-in-out;
}
</style>
